<template>
  <div>
    <b-nav-item-dropdown
      ref="dropdown"
      right
      toggle-class="d-flex align-items-center dropdown-user-link"
      class="dropdown-user"
    >
      <template #button-content>
        <div class="d-sm-flex d-none user-nav">
          <p class="user-name font-weight-bolder mb-0">
            {{ user.name }}
          </p>
          <span class="user-status">{{ user.restaurant_name }} {{ user.business_id }}</span>
        </div>
        <b-avatar
          size="40"
          variant="light-primary"
          :src="user.logo"
          class="badge-minimal"
        />
      </template>

      <b-dropdown-text link-class="d-flex align-items-center">
        <dark-toggler />
      </b-dropdown-text>
      <b-dropdown-text link-class="d-flex align-items-center">
        <notifications-toggle />
      </b-dropdown-text>
      <template>
        <b-dropdown-form
          class="d-block d-lg-none"
          link-class="d-flex align-items-center"
        >
          <current-branch-select :disabled="!isUserHasType(USER_TYPES.BRANCH_ADMIN)" />
        </b-dropdown-form>
      </template>

      <template v-if="isUserHasType(USER_TYPES.OWNER)">
        <b-dropdown-item
          link-class="d-flex align-items-center"
          :to="{ name: 'admin:profile' }"
        >
          <feather-icon
            size="16"
            icon="UserIcon"
            class="mr-50"
          />
          <span>{{ $t('profile.my-profile') }}</span>
        </b-dropdown-item>

        <b-dropdown-item
          :to="{ name: 'billing:manage-subscription' }"
          link-class="d-flex align-items-center"
        >
          <feather-icon
            size="16"
            icon="CopyIcon"
            class="mr-50"
          />
          <span>{{ $t('subscription.manage-subscription') }}</span>
        </b-dropdown-item>

        <b-dropdown-item
          :to="{ name: 'billing:billing-information' }"
          link-class="d-flex align-items-center"
        >
          <feather-icon
            size="16"
            icon="FileTextIcon"
            class="mr-50"
          />
          <span>{{ $t('subscription.billing-info') }}</span>
        </b-dropdown-item>

        <b-dropdown-item
          :to="{ name: 'billing:billing-history' }"
          link-class="d-flex align-items-center"
        >
          <feather-icon
            size="16"
            icon="FileIcon"
            class="mr-50"
          />
          <span>{{ $t('subscription.billing-history') }}</span>
        </b-dropdown-item>
        <b-dropdown-item
          v-if="showDynamicLinks"
          :to="{ name: 'admin:dynamic-links' }"
          link-class="d-flex align-items-center"
        >
          <feather-icon
            size="16"
            icon="LinkIcon"
            class="mr-50"
          />
          <span>{{ $t('urls.title') }}</span>
        </b-dropdown-item>
      </template>

      <b-dropdown-item
        v-check-type="USER_TYPES.BRANCH_ADMIN"
        class="d-block d-lg-none"
        link-class="d-flex align-items-center"
        :to="{name: 'admin:store'}"
      >
        <integration-icon class="mr-50" />
        <span>{{ $t('integrations.integrations') }}</span>
      </b-dropdown-item>

      <b-dropdown-item
        v-check-type="USER_TYPES.BRANCH_ADMIN"
        class="d-block d-lg-none"
        link-class="d-flex align-items-center"
        :to="{name: 'admin:store'}"
      >
        <puzzle-icon class="mr-50" />
        <span>{{ $t('subscription.add-ons') }}</span>
      </b-dropdown-item>

      <b-dropdown-item
        class="d-block d-lg-none"
        link-class="d-flex align-items-center"
        @click="openFreshchat"
      >
        <feather-icon
          size="16"
          icon="HelpCircleIcon"
          class="mr-50"
        />
        <span>{{ $t('help.help') }}</span>
      </b-dropdown-item>

      <b-dropdown-item
        v-if="isSuperAdmin"
        link-class="d-flex align-items-center"
        @click="toggleSuperAdminView"
      >
        <feather-icon
          size="16"
          icon="AirplayIcon"
          class="mr-50"
        />
        <span>{{ onSuperAdminRoutes ? $t('navigation.admin-panel') : $t('navigation.super-admin-panel') }}</span>
      </b-dropdown-item>
      <b-dropdown-item
        v-if="!impersonate && isPrimaryAccount"
        class="d-block"
        link-class="d-flex align-items-center"
        @click="openSwitchPopup"
      >
        <feather-icon
          size="16"
          icon="RepeatIcon"
          class="mr-50"
        />
        <span>{{ $t('users.switch-account') }}</span>
      </b-dropdown-item>
      <b-dropdown-divider />

      <b-dropdown-item
        link-class="d-flex align-items-center text-primary"
        @click="logout"
      >
        <feather-icon
          size="16"
          icon="LogOutIcon"
          class="mr-50"
        />
        <span>{{ $t('auth.logout') }}</span>
      </b-dropdown-item>

    </b-nav-item-dropdown>
    <switch-account-popup
      :value="showSwitchPopup"
      @close="closeSwitchPopUp"
    />
  </div>
</template>

<script>
import {
  BAvatar, BDropdownDivider, BDropdownItem, BDropdownForm, BNavItemDropdown, BDropdownText, VBToggle,
  VBModal,
} from 'bootstrap-vue'
import DarkToggler from '@core/layouts/components/app-navbar/components/DarkToggler.vue'
import { mapGetters } from 'vuex'
import NotificationsToggle from '@core/layouts/components/app-navbar/components/NotificationsToggle'
import SwitchAccountPopup from '@/views/admin/user/SwitchAccountPopup'
import PermissionMixin from '@/mixins/permission-mixin'
import CurrentBranchSelect from '@/@okmenu/components/utils/CurrentBranchSelect'
import FreshchatService from '@/services/FreshchatService'
import MarketingService from '@/services/marketing-service'
import StorageService from '@/services/storage-service'
import IntegrationIcon from '@/@okmenu/icons/IntegrationIcon'
import PuzzleIcon from '@/@okmenu/icons/PuzzleIcon'
import ImpersonateService from '@/services/impersonate-service'

export default {
  name: 'ProfileActionsDropdown',
  components: {
    SwitchAccountPopup,
    PuzzleIcon,
    IntegrationIcon,
    CurrentBranchSelect,
    DarkToggler,
    NotificationsToggle,
    BNavItemDropdown,
    BDropdownItem,
    BDropdownText,
    BDropdownDivider,
    BDropdownForm,
    BAvatar,
  },
  directives: {
    'b-toggle': VBToggle,
    'b-modal': VBModal,
  },
  mixins: [PermissionMixin],
  data() {
    return {
      showSwitchPopup: false,
      impersonate: false,
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/user',
      isSuperAdmin: 'permissions/isSuperAdmin',
      onSuperAdminRoutes: 'navigation/isSuperAdminRoute',
      currentBranchId: 'branches/currentBranchId',
      isPrimaryAccount: 'permissions/isPrimaryAccount',
    }),
    showDynamicLinks() {
      return this.user?.hasDynamicLinks ?? false
    },
  },
  watch: {
    currentBranchId() {
      this.$refs.dropdown.hide()
    },
  },
  mounted() {
    this.impersonate = ImpersonateService.inImpersonateMode
  },
  methods: {
    logout() {
      console.log('Bye 👋')
      this.$store.dispatch('auth/LOGOUT')
      MarketingService.storeCampaign(null)
      StorageService.removeItem('okmenu_route_after_auth')
      window.location.replace(this.$router.resolve({ name: 'login' }).href)
    },
    toggleSuperAdminView() {
      if (this.onSuperAdminRoutes) {
        this.$router.push({ name: 'admin:home' }).catch(() => { })
      } else {
        this.$router.push({ name: 'super:home' })
      }
    },
    openFreshchat() {
      FreshchatService.openFreshchat()
    },
    openSwitchPopup() {
      this.showSwitchPopup = true
    },
    closeSwitchPopUp() {
      this.showSwitchPopup = false
    },
  },
}
</script>
